// =============================================================================
// src/features/briefing/briefing-data.ts — Dados do "Briefing do dia" (puro).
//
// Proveniencia: criado na reforma institucional 2026-06 (decisao D2); REFORMADO
//   em 2026-07-28 a pedido do usuario ("atualize-a com dados reais, sem
//   alertas, troque os cards"): sairam os 4 KPIs do mundo-mock (municipios
//   liderados/Reputation Index/sentimento/eleitorado) e a lista de alertas;
//   entram 4 analises do banco VIVO social_listening, lidas do
//   analytics-live-store (hidratado ANTES do mountRouteLayer no bootstrap —
//   leitura sincrona no render, sem re-render). A manchete virou sintese
//   DETERMINISTICA dos mesmos numeros: a manchete IA do gold.daily_briefing
//   estava parada em 2026-06-24 (fiacao removida em sentinel-overlay/client).
// Fontes dos numeros:
//   - Mencoes rastreadas/Interacoes: getAnalyticsKpis() — mesmo strip do
//     Analytics (delta janela 30d x 30d anteriores), casado pelos labels-
//     contrato de analytics-types.
//   - Tema em destaque: getAnalyticsThemeWindow() (sl_serving_v_watch_window_30d).
//   - Voz mais ativa: getAnalyticsInfluencers() (sl_serving_v_author_window_30d).
//   - Mini-mapa: DERIVADO de EXPLORE_DATASET (TSE) — inalterado.
// Data do eyebrow: REAL (TZ America/Manaus, mesma fronteira de dia dos rollups
//   do backend); injetada por parametro p/ testes deterministicos. A regra
//   antiga "sem new Date()" valia para o mundo-mock — o briefing agora e
//   superficie real e o default new Date() vive no markup builder.
// =============================================================================

import type { ElectoralYear } from '../../app/types';
import { EXPLORE_DATASET } from '../../data';
import { PARTY_COLORS } from '../explore/constants';
import {
  getAnalyticsInfluencers,
  getAnalyticsKpis,
  getAnalyticsLedeTotals,
  getAnalyticsThemeWindow,
} from '../analytics/analytics-live-store';
import {
  ANALYTICS_KPI_INTERACTIONS_LABEL,
  ANALYTICS_KPI_MENTIONS_LABEL,
  type OmarAnalyticsWindowSlice,
} from '../analytics/analytics-types';
import { analyticsPtBrInteger } from '../../data/analytics-overlay-format';

/** Ano eleitoral de referencia do mini-mapa (ultimo pleito municipal). */
export const BRIEFING_ELECTION_YEAR: ElectoralYear = '2024';

/** Fuso do dia do briefing — mesma fronteira dos rollups diarios do backend. */
const BRIEFING_DATE_TIME_ZONE = 'America/Manaus';

/**
 * Data REAL do eyebrow em pt-BR: "segunda-feira, 28 de julho de 2026".
 *
 * @example briefingDateLabel(new Date('2026-07-28T12:00:00-04:00'))
 */
export function briefingDateLabel(reference: Date): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'full',
    timeZone: BRIEFING_DATE_TIME_ZONE,
  }).format(reference);
}

// 2026-09-20: briefingProvenanceDateLabel foi REMOVIDA daqui. Ela formatava o
// relogio do render (new Date()) para o rodape, o que fazia o Briefing escrever a
// data de hoje mesmo com a coleta parada e mesmo na base demonstrativa. O rodape
// passou a citar a ultima COLETA, via analyticsProvenanceUpdateLabel — funcao
// unica, compartilhada com o rodape do Analytics (data/analytics-overlay-scope).
// briefingDateLabel (o eyebrow, logo acima) CONTINUA lendo o relogio de proposito:
// ali a data e a da leitura, nao a do dado.

/** Direcao de tendencia exibida num KPI do briefing. */
export type BriefingTrendDir = 'up' | 'down' | 'flat';

/** Natureza do valor do KPI: numero tabular ou rotulo textual (tema/voz). */
export type BriefingKpiValueKind = 'numeric' | 'text';

/** KPI do briefing: rotulo, valor, tendencia e nota de rodape. */
export interface BriefingKpi {
  readonly label: string;
  readonly value: string;
  readonly valueKind: BriefingKpiValueKind;
  readonly trendDir: BriefingTrendDir;
  readonly trendText: string;
  readonly foot: string;
}

/** Par (label-contrato do Analytics, rotulo acentuado exibido no briefing). */
const BRIEFING_VOLUME_KPI_CARDS: readonly { sourceLabel: string; displayLabel: string }[] = [
  { sourceLabel: ANALYTICS_KPI_MENTIONS_LABEL, displayLabel: 'Menções rastreadas' },
  { sourceLabel: ANALYTICS_KPI_INTERACTIONS_LABEL, displayLabel: 'Interações' },
];

/** KPI de volume vindo do strip do Analytics (live: 30d x 30d; mock: mensal). */
function briefingVolumeKpi(sourceLabel: string, displayLabel: string): BriefingKpi {
  const kpi = getAnalyticsKpis().find((candidate) => candidate.label === sourceLabel);
  /* v8 ignore next 2 -- inalcancavel: mock e patch live (que mapeia sobre o mock) sempre contem os dois labels-contrato */
  if (!kpi)
    return { label: displayLabel, value: '—', valueKind: 'numeric', trendDir: 'flat', trendText: 'sem dado', foot: 'indicador indisponível' };
  return { label: displayLabel, value: kpi.value, valueKind: 'numeric', trendDir: kpi.trend, trendText: kpi.delta, foot: kpi.context };
}

/** Fallback demonstrativo do tema (mundo-mock do Analytics: BR-319). */
const BRIEFING_FALLBACK_THEME: OmarAnalyticsWindowSlice = {
  sliceLabel: 'BR-319',
  sliceDetail: 'tema dominante do periodo demonstrativo',
  mentions: 412,
  sharePct: 34,
};

/** Tema com mais mencoes na janela 30d (sl_serving_v_watch_window_30d). */
function kpiTemaEmDestaque(): BriefingKpi {
  const tema = getAnalyticsThemeWindow()[0] ?? BRIEFING_FALLBACK_THEME;
  return {
    label: 'Tema em destaque',
    value: tema.sliceLabel,
    valueKind: 'text',
    trendDir: 'flat',
    trendText: `${tema.sharePct}% das menções com tema`,
    foot: `${analyticsPtBrInteger(tema.mentions)} menções na janela monitorada`,
  };
}

/** Autor com mais mencoes na janela (SQL ordena no live; mock = 1º da tabela). */
function kpiVozMaisAtiva(): BriefingKpi {
  const voz = getAnalyticsInfluencers()[0];
  /* v8 ignore next 2 -- inalcancavel: o mock nunca e vazio e applyAnalyticsLivePatch descarta arrays vazios */
  if (!voz)
    return { label: 'Voz mais ativa', value: '—', valueKind: 'text', trendDir: 'flat', trendText: 'sem dado', foot: 'sem autores na janela' };
  return {
    label: 'Voz mais ativa',
    value: voz.influencerName,
    valueKind: 'text',
    trendDir: 'flat',
    trendText: `${analyticsPtBrInteger(voz.mentions)} menções`,
    foot: `${voz.platform} · maior volume na janela`,
  };
}

/** Os 4 KPIs da faixa: menções, interações, tema líder e voz mais ativa. */
export function buildBriefingKpis(): readonly BriefingKpi[] {
  return [
    ...BRIEFING_VOLUME_KPI_CARDS.map((card) => briefingVolumeKpi(card.sourceLabel, card.displayLabel)),
    kpiTemaEmDestaque(),
    kpiVozMaisAtiva(),
  ];
}

/**
 * Manchete do dia: sintese deterministica dos numeros do social_listening
 * (mencoes da janela + tema lider + voz mais ativa). Caminho UNICO live/mock —
 * no fallback estatico os getters devolvem o mundo-mock e o rodape de
 * proveniencia ja nomeia "base demonstrativa".
 */
export function buildBriefingHeadline(): string {
  const mencoes = getAnalyticsLedeTotals().mentionsLabel;
  const tema = getAnalyticsThemeWindow()[0] ?? BRIEFING_FALLBACK_THEME;
  const voz = getAnalyticsInfluencers()[0];
  /* v8 ignore next -- mesmo fallback inalcancavel do kpiVozMaisAtiva */
  const vozLabel = voz ? `${voz.influencerName} (${voz.platform})` : 'sem voz destacada';
  return (
    `${mencoes} menções monitoradas nas redes e na imprensa. ` +
    `${tema.sliceLabel} concentra o debate; a voz mais ativa do período é ${vozLabel}.`
  );
}

/** Contagem de municipios liderados por um partido no ano (1º colocado). */
export interface BriefingPartyLead {
  readonly party: string;
  readonly count: number;
}

/** Ranking de partidos por municipios liderados no ano, ordem decrescente. */
export function briefingPartyLeads(year: ElectoralYear): readonly BriefingPartyLead[] {
  const counts = new Map<string, number>();
  Object.values(EXPLORE_DATASET.munis).forEach((mun) => {
    /* v8 ignore next -- fallback exigido por noUncheckedIndexedAccess; a serie el 2014-2024 e completa no dataset real */
    const winner = mun.el[year]?.p ?? 'desconhecido';
    counts.set(winner, (counts.get(winner) ?? 0) + 1);
  });
  return [...counts.entries()]
    .map(([party, count]) => ({ party, count }))
    .sort((a, b) => b.count - a.count);
}

/** Item de legenda do mini-mapa: partido, cor e municipios liderados. */
export interface BriefingMapLegendItem {
  readonly party: string;
  readonly color: string;
  readonly count: number;
}

/** Top-4 partidos do mini-mapa com suas cores de dado (PARTY_COLORS). */
export function buildBriefingMapLegend(): readonly BriefingMapLegendItem[] {
  return briefingPartyLeads(BRIEFING_ELECTION_YEAR)
    .slice(0, 4)
    /* v8 ignore next -- fallback exigido por noUncheckedIndexedAccess; todo vencedor do dataset tem cor em PARTY_COLORS */
    .map(({ party, count }) => ({ party, count, color: PARTY_COLORS[party] ?? '#B7B3AA' }));
}
